// src/components/menu/CategoriesComponent.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaUtensils } from 'react-icons/fa';
import type { Branch, MainCategory } from '~/types/new_types';
import { getCategoryDescription, getCategoryName } from '~/utils/categoryHelper';
import { useBranch } from '~/contexts/BranchContext';
import { BiSolidCategory } from 'react-icons/bi';

interface CategoriesComponentProps {
	// categories: MainCategory[];
	// theme: Branch['theme'];
	activeCategory?: string;
	onCategorySelect?: (categoryId: string) => void;
	layout?: 'grid' | 'list' | 'tabs' | 'dropdown';
	showItemCount?: boolean;
	showImages?: boolean;
}

const CategoriesComponent: React.FC<CategoriesComponentProps> = ({
	// categories,
	// theme,
	activeCategory,
	onCategorySelect,
	layout = 'list',
	showItemCount = true,
	showImages = true
}) => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';
	const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

	const { categories, theme } = useBranch()
	// showing the categories even if they don't have subcategories but have items
	const filteredCategories = categories.filter(cat => {
		if (!cat.is_published) return false;
		const hasDirectItems = Array.isArray(cat.items) && cat.items.length > 0;
		const hasPublishedSubcategories = cat.subCategories.some(
			(sub) => sub.is_published && Array.isArray(sub.items) && sub.items.length > 0
		);
		return hasDirectItems || hasPublishedSubcategories;
	})
	const resolvedActiveCategory = activeCategory ?? 'menu';
	// const filteredCategories = categories

	const toggleCategory = (categoryId: string) => {
		setExpandedCategories(prev => ({
			...prev,
			[categoryId]: !prev[categoryId]
		}));
	};

	const handleCategoryClick = (categoryId: string) => {
		if (onCategorySelect) {
			onCategorySelect(categoryId);
		}
	};

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1
			}
		}
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				type: "spring" as const,
				stiffness: 100,
				damping: 15
			}
		}
	};

	const contentVariants = {
		hidden: { opacity: 0, height: 0, overflow: 'hidden' },
		visible: {
			opacity: 1,
			height: 'auto',
			overflow: 'visible',
			transition: { duration: 0.3 }
		},
		exit: {
			opacity: 0,
			height: 0,
			overflow: 'hidden',
			transition: { duration: 0.2 }
		}
	};

	// Grid Layout
	const renderGridLayout = () => (
		<motion.div
			className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
			variants={containerVariants}
			initial="hidden"
			animate="visible"
		>
			{filteredCategories.map((category) => (
				<motion.div
					key={category.id}
					variants={itemVariants}
					className="group cursor-pointer"
					onClick={() => handleCategoryClick(category.id)}
					whileHover={{ y: -5 }}
				>
					<div
						className="rounded-2xl overflow-hidden transition-all duration-300"
						style={{
							backgroundColor: resolvedActiveCategory === category.id
								? theme?.primaryColor + '20'
								: theme?.cardColor,
							border: theme?.cardStyle === 'bordered'
								? `2px solid ${resolvedActiveCategory === category.id ? theme?.primaryColor : theme?.borderColor}`
								: 'none',
							boxShadow: theme?.cardStyle === 'shadow'
								? `0 10px 25px -5px ${theme?.primaryColor}20`
								: 'none',
							borderRadius: theme?.borderRadius
						}}
					>
						{/* Category Image */}
						{showImages && (
							<div className="relative aspect-video overflow-hidden">
								<motion.img
									src={category.image_url ?? "/images/no-image-available.jpg"}
									alt={getCategoryName(category, i18n.language)}
									className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
									onError={(e) => {
										const target = e.target as HTMLImageElement;
										target.src = "/images/broken-image.jpg";
									}}
								/>
								{/* <div
									className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
								/> */}

								{/* Item Count Badge */}
								{/* {showItemCount && category.itemsCount > 0 && (
									<div
										className="absolute top-3 end-3 px-3 py-1 rounded-full text-sm font-semibold"
										style={{
											backgroundColor: theme?.primaryColor,
											color: '#fff',
											borderRadius: theme?.borderRadius
										}}
									>
										{category.itemsCount} {category.itemsCount === 1 ? t('menu.item') : t('menu.items')}
									</div>
								)} */}
							</div>
						)}

						{/* Category Content */}
						<div className="p-6">
							<div className="flex items-center justify-between mb-3">
								<h3
									className={`${theme?.titleFontSize} ${theme?.boldTitles ? 'font-bold' : 'font-semibold'}`}
									style={{ color: theme?.textColor }}
								>
									{getCategoryName(category, i18n.language)}
								</h3>

								{!showImages && showItemCount && category.itemsCount > 0 && (
									<span
										className="px-2 py-1 rounded-full text-sm"
										style={{
											backgroundColor: `${theme?.primaryColor}20`,
											color: theme?.primaryColor,
											borderRadius: theme?.borderRadius
										}}
									>
										{category.itemsCount}
									</span>
								)}
							</div>

							{getCategoryDescription(category, i18n.language) && (
								<p
									className="text-sm line-clamp-2 mb-4"
									style={{ color: `${theme?.textColor}80` }}
								>
									{getCategoryDescription(category, i18n.language)}
								</p>
							)}

							{/* Subcategories Preview */}
							{/* {category.subCategories.length > 0 && (
								<div className="flex flex-wrap gap-2">
									{category.subCategories.slice(0, 3).map((subCategory) => (
										<span
											key={subCategory.id}
											className="px-2 py-1 text-xs rounded-full"
											style={{
												backgroundColor: `${theme?.accentColor}20`,
												color: theme?.textColor,
												borderRadius: theme?.borderRadius
											}}
										>
											{getCategoryName(subCategory, i18n.language)}
										</span>
									))}
									{category.subCategories.length > 3 && (
										<span
											className="px-2 py-1 text-xs rounded-full"
											style={{
												backgroundColor: `${theme?.primaryColor}20`,
												color: theme?.primaryColor,
												borderRadius: theme?.borderRadius
											}}
										>
											+{category.subCategories.length - 3} {t('categories.more')}
										</span>
									)}
								</div>
							)} */}
						</div>
					</div>
				</motion.div>
			))}
		</motion.div>
	);

	// List Layout
	const renderListLayout = () => (
		<motion.div
			className="space-y-4"
			variants={containerVariants}
			initial="hidden"
			animate="visible"
		>
			{filteredCategories.map((category) => (
				<motion.div
					key={category.id}
					variants={itemVariants}
					className="group cursor-pointer"
					onClick={() => handleCategoryClick(category.id)}
				>
					<div
						className="flex items-center gap-4 p-4 rounded-xl transition-all duration-300"
						style={{
							backgroundColor: resolvedActiveCategory === category.id
								? theme?.primaryColor + '20'
								: theme?.cardColor,
							border: theme?.cardStyle === 'bordered'
								? `2px solid ${resolvedActiveCategory === category.id ? theme?.primaryColor : theme?.borderColor}`
								: 'none',
							boxShadow: theme?.cardStyle === 'shadow'
								? `0 6px 12px ${theme?.primaryColor}20`
								: 'none',
							borderRadius: theme?.borderRadius
						}}
					>
						{/* Category Image */}
						{showImages && (
							<div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
								<img
									src={category.image_url ?? "/images/no-image-available.jpg"}
									alt={getCategoryName(category, i18n.language)}
									className="w-full h-full object-cover"
									onError={(e) => {
										const target = e.target as HTMLImageElement;
										target.src = "/images/broken-image.jpg";
									}}
								/>
							</div>
						)}

						{/* Category Content */}
						<div className="flex-1 min-w-0">
							<div className="flex items-center justify-between">
								<h3
									className={`${theme?.itemFontSize} ${theme?.boldTitles ? 'font-bold' : 'font-semibold'} truncate`}
									style={{ color: theme?.textColor }}
								>
									{getCategoryName(category, i18n.language)}
								</h3>

								{showItemCount && category.itemsCount > 0 && (
									<span
										className="px-2 py-1 rounded-full text-sm flex-shrink-0 ms-2"
										style={{
											backgroundColor: `${theme?.primaryColor}20`,
											color: theme?.primaryColor,
											borderRadius: theme?.borderRadius
										}}
									>
										{category.itemsCount}
									</span>
								)}
							</div>

							{getCategoryDescription(category, i18n.language) && (
								<p
									className="text-sm line-clamp-1 mt-1"
									style={{ color: `${theme?.textColor}80` }}
								>
									{getCategoryDescription(category, i18n.language)}
								</p>
							)}
						</div>

						{/* Arrow Icon */}
						<div
							className="text-lg"
							style={{ color: theme?.primaryColor }}
						>
							{isRTL ? <FaChevronLeft /> : <FaChevronRight />}
						</div>
					</div>
				</motion.div>
			))}
		</motion.div>
	);

	// Tabs Layout
	const renderTabsLayout = () => {
		const categories = [{ id: 'menu', name: 'All', ar: { name: 'الكل' } } as MainCategory, ...filteredCategories]
		return (
			<div className="flex overflow-y-clip overflow-x-auto gap-2 p-1 no-scrollbar">
				{categories.map((category) => (
					<motion.button
						key={category.id}
						onClick={() => handleCategoryClick(category.id)}
						className="px-2 py-1 rounded-lg flex items-center gap-2 cursor-pointer transition-all duration-300 text-nowrap"
						style={{
							backgroundColor: resolvedActiveCategory === category.id
								? theme?.primaryColor
								: `${theme?.primaryColor}15`,
							color: resolvedActiveCategory === category.id ? '#fff' : theme?.textColor,
							borderRadius: theme?.borderRadius
						}}
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
					>
						<span className={theme?.itemFontSize}>
							{getCategoryName(category, i18n.language)}
						</span>
						{showItemCount && category.itemsCount > 0 && (
							<span
								className="px-1.5 py-0.5 text-xs rounded-full"
								style={{
									backgroundColor: resolvedActiveCategory === category.id
										? 'rgba(255, 255, 255, 0.3)'
										: `${theme?.primaryColor}20`,
									borderRadius: theme?.borderRadius
								}}
							>
								{category.itemsCount}
							</span>
						)}
					</motion.button>
				))}
			</div>
		)
	};

	// Dropdown Layout
	const renderDropdownLayout = () => (
		<motion.div
			className="space-y-2"
			variants={containerVariants}
			initial="hidden"
			animate="visible"
		>
			{filteredCategories.map((category) => (
				<motion.div
					key={category.id}
					variants={itemVariants}
					className="rounded-xl overflow-hidden"
					style={{
						backgroundColor: theme?.cardColor,
						border: theme?.cardStyle === 'bordered' ? `2px solid ${theme?.borderColor}` : 'none',
						boxShadow: theme?.cardStyle === 'shadow' ? `0 4px 12px ${theme?.primaryColor}10` : 'none',
						borderRadius: theme?.borderRadius
					}}
				>
					<motion.button
						onClick={() => toggleCategory(category.id)}
						className="w-full px-6 py-4 flex items-center justify-between cursor-pointer"
						whileHover={{ backgroundColor: `${theme?.primaryColor}10` }}
					>
						<div className="flex items-center gap-3">
							<motion.div
								animate={{ rotate: expandedCategories[category.id] ? 180 : 0 }}
								transition={{ duration: 0.3 }}
							>
								<FaChevronDown size={14} style={{ color: theme?.primaryColor }} />
							</motion.div>
							<h3
								className={`${theme?.itemFontSize} ${theme?.boldTitles ? 'font-bold' : 'font-semibold'}`}
								style={{ color: theme?.textColor }}
							>
								{getCategoryName(category, i18n.language)}
							</h3>
						</div>

						{showItemCount && category.itemsCount > 0 && (
							<span
								className="px-3 py-1 rounded-full text-sm"
								style={{
									backgroundColor: `${theme?.primaryColor}15`,
									color: theme?.textColor,
									borderRadius: theme?.borderRadius
								}}
							>
								{category.itemsCount} {category.itemsCount === 1 ? t('menu.item') : t('menu.items')}
							</span>
						)}
					</motion.button>

					<AnimatePresence>
						{expandedCategories[category.id] && (
							<motion.div
								variants={contentVariants}
								initial="hidden"
								animate="visible"
								exit="exit"
							>
								<div className="px-6 pb-4">
									{getCategoryDescription(category, i18n.language) && (
										<p
											className="text-sm mb-4"
											style={{ color: `${theme?.textColor}80` }}
										>
											{getCategoryDescription(category, i18n.language)}
										</p>
									)}

									{/* Subcategories */}
									{category.subCategories.length > 0 && (
										<div className="space-y-2">
											<h4
												className="text-sm font-semibold mb-2"
												style={{ color: theme?.textColor }}
											>
												{t('categories.subcategories')}:
											</h4>
											<div className="flex flex-wrap gap-2">
												{category.subCategories.map((subCategory) => (
													<span
														key={subCategory.id}
														className="px-3 py-1 text-sm rounded-full"
														style={{
															backgroundColor: `${theme?.accentColor}20`,
															color: theme?.textColor,
															borderRadius: theme?.borderRadius
														}}
													>
														{getCategoryName(subCategory, i18n.language)}
														{showItemCount && subCategory.itemsCount > 0 && (
															<span className="ms-1 opacity-70">
																({subCategory.itemsCount})
															</span>
														)}
													</span>
												))}
											</div>
										</div>
									)}
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</motion.div>
			))}
		</motion.div>
	);

	// Empty State
	if (categories.length === 0) {
		return (
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				className="text-center py-20 rounded-3xl"
				style={{
					backgroundColor: `${theme?.primaryColor}10`,
					borderRadius: theme?.borderRadius
				}}
			>
				<div
					className="text-7xl mb-6 flex justify-center"
					style={{ color: `${theme?.primaryColor}40` }}
				>
					<FaUtensils className="opacity-80" />
				</div>
				<h3
					className="text-2xl font-bold mb-3"
					style={{ color: theme?.textColor }}
				>
					{t('categories.noCategories')}
				</h3>
				<p
					className="max-w-md mx-auto"
					style={{ color: `${theme?.textColor}80` }}
				>
					{t('categories.noCategoriesDescription')}
				</p>
			</motion.div>
		);
	}

	// Render based on layout
	switch (layout) {
		case 'list':
			return renderListLayout();
		case 'tabs':
			return renderTabsLayout();
		case 'dropdown':
			return renderDropdownLayout();
		case 'grid':
		default:
			return renderGridLayout();
	}
};

export default CategoriesComponent;